import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout, DashboardLayout } from './components/Layout';
import { 
  Home, 
  Create, 
  Customize, 
  Download,
  Login,
  Signup,
  Dashboard,
  Mockups,
  Library,
  Assets,
  Brand,
  Support,
  Settings
} from './pages';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ErrorBoundary from './components/ErrorBoundary';
import AuthGuard from './components/AuthGuard';
import AdminGuard from './components/AdminGuard';
import AdminDashboard from './components/Admin/AdminDashboard';
import UserManagement from './components/Admin/UserManagement';
import UserDetail from './components/Admin/UserDetail';
import Statistics from './components/Admin/Statistics';
import AuditLog from './components/Admin/AuditLog';
import AuthDebug from './components/AuthDebug';
import TestAdminRoute from './components/TestAdminRoute';
import { clearCacheOnStartup } from './utils/devCacheClear';
import './utils/forceRefreshProfile';

function AppContent() {
  const { user } = useAuth();

  return (
    <>
      <AuthDebug />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
      </Route>

      {/* Auth Routes */}
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
      <Route path="/signup" element={user ? <Navigate to="/dashboard" /> : <Signup />} />

      {/* Protected Dashboard Routes */}
      <Route 
        path="/dashboard" 
        element={
          <AuthGuard requireAuth={true}>
            <DashboardLayout />
          </AuthGuard>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="create" element={<Create />} />
        <Route path="customize" element={<Customize />} />
        <Route path="download" element={<Download />} />
        <Route path="mockups" element={<Mockups />} />
        <Route path="library" element={<Library />} />
        <Route path="assets" element={<Assets />} />
        <Route path="brand" element={<Brand />} />
        <Route path="support" element={<Support />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      {/* Test Route - No Guards */}
      <Route path="/test-admin" element={<TestAdminRoute />} />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <AdminGuard>
          <AdminDashboard />
        </AdminGuard>
      } />
      <Route path="/admin/statistics" element={
        <AdminGuard>
          <Statistics />
        </AdminGuard>
      } />
      <Route path="/admin/audit" element={
        <AdminGuard>
          <AuditLog />
        </AdminGuard>
      } />
      <Route path="/admin/users" element={
        <AdminGuard>
          <UserManagement />
        </AdminGuard>
      } />
      <Route path="/admin/users/:id" element={
        <AdminGuard>
          <UserDetail />
        </AdminGuard>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
    </>
  );
}

function App() {
  useEffect(() => {
    // Clear cache on startup in development mode
    clearCacheOnStartup();
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <NotificationProvider>
          <AuthProvider>
            <AppContent />
          </AuthProvider>
        </NotificationProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
